<template>
  <div class="block-calendar-month">
    <div class="calendar-header">
      <span class="month-name">{{ monthName }}</span>
      <div class="month-nav">
        <button @click="prevMonth" class="nav-btn" aria-label="Edellinen kuukausi">←</button>
        <button @click="nextMonth" class="nav-btn" aria-label="Seuraava kuukausi">→</button>
      </div>
    </div>

    <div class="calendar-body">
      <!-- Left: grid -->
      <div class="calendar-grid" role="grid">
        <!-- Weekday header row -->
        <span
          v-for="d in WEEKDAYS"
          :key="d"
          class="cell weekday-label"
          role="columnheader"
        >{{ d }}</span>

        <!-- Empty cells before the 1st — one per day the month is offset from Monday -->
        <span
          v-for="n in startPad"
          :key="'pad-' + n"
          class="cell day-pad"
          aria-hidden="true"
        />

        <!-- Day cells -->
        <span
          v-for="day in daysInMonth"
          :key="day"
          class="cell day"
          :class="{
            'has-performance': perfDays.has(day),
            'is-today': isToday(day),
          }"
          role="gridcell"
        >{{ day }}</span>
      </div>

      <!-- Right: performance list -->
      <ul class="performance-list">
        <template v-if="monthPerformances.length">
          <li v-for="p in monthPerformances" :key="p.date + p.title" class="perf-item">
            <a :href="pathName(p.url)" class="perf-title">{{ p.title }}</a>
            <span class="perf-when">{{ formatPerfDate(p.date) }} klo {{ p.time }}</span>
          </li>
        </template>
        <li v-else class="no-perfs">–</li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios'

const props = defineProps(['block'])
const lang = document.documentElement.lang

const WEEKDAYS  = ['Ma','Ti','Ke','To','Pe','La','Su']
const MONTH_NAMES = ['Tammikuu','Helmikuu','Maaliskuu','Huhtikuu','Toukokuu','Kesäkuu',
                     'Heinäkuu','Elokuu','Syyskuu','Lokakuu','Marraskuu','Joulukuu']
const DAY_NAMES = ['Sunnuntai','Maanantai','Tiistai','Keskiviikko','Torstai','Perjantai','Lauantai']

const allProductions = ref([])
const _today = new Date()
const displayYear  = ref(_today.getFullYear())
const displayMonth = ref(_today.getMonth() + 1)   // 1–12

const monthName = computed(() => MONTH_NAMES[displayMonth.value - 1])

// Number of empty cells before day 1.
// JS getDay(): 0=Sun 1=Mon … 6=Sat → convert to 0=Mon … 6=Sun (Finnish week)
const startPad = computed(() => {
  const firstDay = new Date(displayYear.value, displayMonth.value - 1, 1).getDay()
  return (firstDay + 6) % 7
})

// Days in the displayed month
const daysInMonth = computed(() =>
  new Date(displayYear.value, displayMonth.value, 0).getDate()
)

// All performances falling in the displayed month, sorted by date
const monthPerformances = computed(() => {
  const prefix = `${displayYear.value}-${String(displayMonth.value).padStart(2, '0')}`
  const result = []
  for (const prod of allProductions.value) {
    for (const p of yamlToArray(prod.performances)) {
      if (p?.date?.startsWith(prefix)) {
        result.push({ ...p, title: prod.title, url: prod.url })
      }
    }
  }
  return result.sort((a, b) => a.date.localeCompare(b.date))
})

// Set of day-of-month numbers that have at least one performance
const perfDays = computed(() =>
  new Set(monthPerformances.value.map(p => Number(p.date.split('-')[2])))
)

function isToday(day) {
  return _today.getFullYear() === displayYear.value &&
    _today.getMonth() + 1 === displayMonth.value &&
    _today.getDate() === day
}

function prevMonth() {
  if (displayMonth.value === 1) { displayYear.value--;  displayMonth.value = 12 }
  else displayMonth.value--
}

function nextMonth() {
  if (displayMonth.value === 12) { displayYear.value++; displayMonth.value = 1 }
  else displayMonth.value++
}

function formatPerfDate(str) {
  const d = new Date(str + 'T12:00:00')
  if (isNaN(d)) return str
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`
}

onMounted(async () => {
  const lc = (f) => lang ? `page.content("${lang}").${f}` : `page.content.${f}`
  const { data } = await axios.post('/api/cachedKQL', {
    select: {
      productions: {
        query: 'site.find("ohjelmisto").children.listed',
        select: {
          title: lc('title'),
          url: lang ? `page.url("${lang}")` : true,
          performances: lc('performances'),
        }
      }
    }
  })
  allProductions.value = cleanUpJSON(data.result?.productions || [])
})
</script>

<style scoped>
.block-calendar-month {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* ── Header ── */
.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.month-name {
  font-weight: 500;
  font-size: 1.1rem;
  letter-spacing: 0.03em;
}

.month-nav {
  display: flex;
  gap: 0.5rem;
}

.nav-btn {
  background: none;
  border: 1px solid var(--fg-30, rgba(0,0,0,0.3));
  padding: 0.2rem 0.7rem;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1.6;
  color: inherit;
  transition: background 150ms;
}
.nav-btn:hover { background: var(--fg-10, rgba(0,0,0,0.06)); }

/* ── Body: grid + list side by side ── */
.calendar-body {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2rem;
  align-items: start;
}

@media (max-width: 580px) {
  .calendar-body { grid-template-columns: 1fr; }
}

/* ── Calendar grid ── */
/*
  7 equal columns. First 7 cells = weekday labels, then
  startPad empty cells, then 28–31 day cells. CSS grid
  wraps automatically → weeks form natural rows.
*/
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 2.2rem);
  row-gap: 2px;
}

/* All cells share the same box model so columns stay aligned */
.cell {
  width: 2.2rem;
  height: 2.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
}

.weekday-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.45;
  border-bottom: 1px solid var(--fg-20, rgba(0,0,0,0.15));
  margin-bottom: 2px;
}

/* Pad cells are identical in size to day cells but show nothing */
.day-pad {
  /* intentionally empty */
}

.day {
  border-radius: 50%;
  cursor: default;
  transition: background 120ms;
}

.day.has-performance {
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.day.is-today {
  border: 1px solid currentColor;
}

.day.has-performance.is-today {
  background: var(--fg, currentColor);
  color: var(--bg, #fff);
  border: none;
}

/* ── Performance list ── */
.performance-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.no-perfs {
  opacity: 0.35;
}

.perf-item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.perf-title {
  font-weight: 500;
  font-size: 0.95rem;
}

.perf-when {
  font-size: 0.82rem;
  opacity: 0.6;
}
</style>
